/**
 * GlobalModalHost — Renders the currently active global modal.
 *
 * Mounted in AppLayout (standalone mode). Reads from modalStore to determine
 * which modal panel to display inside a UiModal wrapper.
 *
 * Phase 1: Stub panels with placeholder content.
 * Phase 2: Full panel implementations ported from AngularJS.
 */

import { lazy, Suspense } from 'react';
import { UiModal } from '@/ui/UiModal';
import { UiLoader } from '@/ui';
import { useModalStore, type ModalName } from '@/store/modalStore';

const WorkOrdersPanel = lazy(() => import('./WorkOrdersPanel'));
const PropertyTextPanel = lazy(() => import('./PropertyTextPanel'));
const PropertyImagePanel = lazy(() => import('./PropertyImagePanel'));
const NotesPanel = lazy(() => import('./NotesPanel'));
const VendorTextPanel = lazy(() => import('./VendorTextPanel'));
const AuditBillingPanel = lazy(() => import('./AuditBillingPanel'));

const MODAL_CONFIG: Record<
  ModalName,
  { title: string; size?: 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '4xl'; className?: string }
> = {
  workOrders: { title: 'Work Orders', size: 'xl' },
  propertyText: { title: 'Property Text', size: '4xl' },
  propertyImage: { title: 'Property Images', size: '4xl' },
  // Custom width for the notes modal. The `!` overrides the `size` preset's
  // max-width — change the px value here to resize. (`size` is kept as a fallback.)
  notes: { title: 'Notes', size: '4xl', className: '!max-w-[80%] !min-h-[80%]' },
  vendorText: { title: 'Vendor Text', size: 'lg' },
  auditBilling: { title: 'Audit Billing Details', size: 'xl' },
};

const MODAL_PANELS: Record<ModalName, React.LazyExoticComponent<() => JSX.Element>> = {
  workOrders: WorkOrdersPanel,
  propertyText: PropertyTextPanel,
  propertyImage: PropertyImagePanel,
  notes: NotesPanel,
  vendorText: VendorTextPanel,
  auditBilling: AuditBillingPanel,
};

// Panels that render their own UiModal (need custom header chrome that
// MODAL_CONFIG can't express — property/loan info in the header bar, etc.).
const SELF_RENDERED_MODALS: ReadonlySet<ModalName> = new Set<ModalName>([
  'propertyText',
  'workOrders',
  'auditBilling',
  'vendorText',
]);

export function GlobalModalHost() {
  const openModal = useModalStore((s) => s.openModal);
  const close = useModalStore((s) => s.close);

  if (!openModal) return null;

  const config = MODAL_CONFIG[openModal];
  const Panel = MODAL_PANELS[openModal];

  if (SELF_RENDERED_MODALS.has(openModal)) {
    return (
      <Suspense fallback={<UiLoader />}>
        <Panel />
      </Suspense>
    );
  }

  return (
    <UiModal
      isOpen
      onClose={close}
      title={config.title}
      size={config.size}
      className={config.className}
    >
      <Suspense fallback={<UiLoader />}>
        <Panel />
      </Suspense>
    </UiModal>
  );
}
